import { useState, createContext, useContext, useCallback, type ReactNode } from 'react'
import {
  Button, Dialog, DialogActions, DialogContent, DialogTitle,
  Stack, Typography,
} from '@mui/material'
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'

// ─── Types ──────────────────────────────────────────────────────────────────

type Variant = 'danger' | 'warning' | 'info'

interface ConfirmOptions {
  title: string
  message: string | ReactNode
  /** Button label — defaults to "Confirm" */
  confirmLabel?: string
  /** Cancel button label — defaults to "Cancel" */
  cancelLabel?: string
  /** Visual style — "danger" shows red, "warning" amber, "info" neutral */
  variant?: Variant
}

interface ConfirmCtx {
  /**
   * Show a confirm dialog and return a Promise<boolean>.
   *
   * ```tsx
   * const confirm = useConfirm()
   * const ok = await confirm({
   *   title: 'Delete site?',
   *   message: 'This cannot be undone.',
   *   confirmLabel: 'Delete',
   *   variant: 'danger',
   * })
   * if (ok) deleteMutation.mutate()
   * ```
   */
  (options: ConfirmOptions): Promise<boolean>
}

// ─── Context ────────────────────────────────────────────────────────────────

const Ctx = createContext<ConfirmCtx>(() => Promise.resolve(false))

export const useConfirm = () => useContext(Ctx)

// ─── Provider ───────────────────────────────────────────────────────────────

interface State extends ConfirmOptions {
  resolve: (value: boolean) => void
}

export function ConfirmDialogProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<State | null>(null)

  const confirm = useCallback((options: ConfirmOptions): Promise<boolean> => {
    return new Promise<boolean>((resolve) => {
      setState({ ...options, resolve })
    })
  }, [])

  function handleClose(result: boolean) {
    state?.resolve(result)
    setState(null)
  }

  return (
    <Ctx.Provider value={confirm}>
      {children}
      {state && (
        <ConfirmDialogUI
          open
          title={state.title}
          message={state.message}
          confirmLabel={state.confirmLabel}
          cancelLabel={state.cancelLabel}
          variant={state.variant}
          onConfirm={() => handleClose(true)}
          onCancel={() => handleClose(false)}
        />
      )}
    </Ctx.Provider>
  )
}

// ─── Dialog UI ──────────────────────────────────────────────────────────────

const VARIANT_CONFIG: Record<Variant, {
  icon: React.ElementType
  iconColor: string
  iconBg: string
  confirmColor: 'error' | 'warning' | 'primary'
}> = {
  danger:  { icon: DeleteOutlineIcon,       iconColor: '#DC2626', iconBg: 'rgba(220,38,38,0.10)',  confirmColor: 'error' },
  warning: { icon: WarningAmberOutlinedIcon, iconColor: '#D97706', iconBg: 'rgba(217,119,6,0.10)', confirmColor: 'warning' },
  info:    { icon: InfoOutlinedIcon,         iconColor: '#0D9488', iconBg: 'rgba(13,148,136,0.10)', confirmColor: 'primary' },
}

function ConfirmDialogUI({
  open, title, message, confirmLabel = 'Confirm', cancelLabel = 'Cancel',
  variant = 'danger', onConfirm, onCancel,
}: {
  open: boolean
  title: string
  message: string | ReactNode
  confirmLabel?: string
  cancelLabel?: string
  variant?: Variant
  onConfirm: () => void
  onCancel: () => void
}) {
  const v = VARIANT_CONFIG[variant]
  const Icon = v.icon

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2.5, overflow: 'hidden' } }}
    >
      <DialogTitle sx={{ px: 3, pt: 3, pb: 0 }}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Stack
            alignItems="center"
            justifyContent="center"
            sx={{
              width: 44, height: 44, borderRadius: '50%',
              bgcolor: v.iconBg, color: v.iconColor, flexShrink: 0,
            }}
          >
            <Icon sx={{ fontSize: 22 }} />
          </Stack>
          <Typography sx={{ fontSize: 17, fontWeight: 700, letterSpacing: '-0.015em' }}>
            {title}
          </Typography>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ px: 3, pt: 2, pb: 1 }}>
        {typeof message === 'string' ? (
          <Typography sx={{ fontSize: 13.5, color: 'text.secondary', lineHeight: 1.6, ml: 7 }}>
            {message}
          </Typography>
        ) : (
          <>{message}</>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, pt: 1.5 }}>
        <Button
          onClick={onCancel}
          sx={{ fontSize: 13, fontWeight: 600, color: 'text.secondary', px: 2 }}
        >
          {cancelLabel}
        </Button>
        <Button
          variant="contained"
          color={v.confirmColor}
          onClick={onConfirm}
          sx={{ fontSize: 13, fontWeight: 600, px: 2.5 }}
        >
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
